import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { COMPANY, HERO_IMAGE } from "../data/content";
import { scrollToSection } from "./Navbar";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const gridY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="blueprint-grid relative overflow-hidden bg-paper pt-28 sm:pt-32">
      <motion.div style={{ y: gridY }} className="pointer-events-none absolute inset-0 blueprint-grid" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 pb-16 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-24">
        <div className="flex flex-col justify-center lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-electric"
            data-testid="hero-overline"
          >
            <MapPin size={13} /> {COMPANY.city} · Est. {COMPANY.established}
          </motion.p>

          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter text-navy sm:text-7xl lg:text-8xl" data-testid="hero-heading">
            <Line delay={0.35}>We design.</Line>
            <Line delay={0.5}>We build.</Line>
            <Line delay={0.65}>
              <span className="text-electric">We deliver.</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subtext"
          >
            {COMPANY.name} — interior consulting, building &amp; civil structure consulting,
            project management and government estimation services. Engineered with
            international standards, built in Bihar.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-projects-button"
              onClick={() => scrollToSection("#projects")}
              className="hard-shadow-sm border border-navy bg-navy px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-transform duration-200 hover:-translate-y-0.5"
            >
              View Projects
            </button>
            <button
              data-testid="hero-enquire-button"
              onClick={() => scrollToSection("#contact")}
              className="border border-navy bg-white px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-navy transition-colors duration-200 hover:bg-electric hover:text-white hover:border-electric"
            >
              Enquire Now
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-14 grid max-w-xl grid-cols-3 divide-x divide-navy/15 border-y border-navy/15"
            data-testid="hero-stats"
          >
            {[
              ["04", "Service Verticals"],
              ["EPC / PMC", "Delivery Models"],
              ["Govt + Pvt", "Project Experience"],
            ].map(([v, l]) => (
              <div key={l} className="px-4 py-4 first:pl-0">
                <p className="font-display text-xl font-extrabold text-navy sm:text-2xl">{v}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="hard-shadow relative overflow-hidden border border-navy bg-navy">
              <motion.img
                src={HERO_IMAGE}
                alt="Architectural blueprint of a multi-story building"
                data-testid="hero-image"
                style={{ y: imgY, scale: 1.15 }}
                className="h-[420px] w-full object-cover sm:h-[520px]"
              />
              <div className="absolute bottom-0 left-0 flex w-full items-center justify-between bg-navy/90 px-4 py-3 backdrop-blur-sm">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-200">
                  Dwg. No. 001 — Concept to Keys
                </span>
                <span className="font-mono text-[10px] text-blue-300">SCALE 1:1</span>
              </div>
            </div>
            <div className="absolute -left-4 -top-4 hidden h-16 w-16 border-l-2 border-t-2 border-electric lg:block" />
            <div className="absolute -bottom-4 -right-4 hidden h-16 w-16 border-b-2 border-r-2 border-electric lg:block" />
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-navy/50 transition-colors hover:text-electric lg:block"
        aria-label="Scroll to services"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.button>
    </section>
  );
};
